import { useCallback, useEffect, useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { UploadSimple, Eye, Trash, DownloadSimple, FloppyDisk } from "@phosphor-icons/react";

const apriBlob = (blob, nome, scarica) => {
  const url = URL.createObjectURL(blob);
  if (scarica) {
    const a = document.createElement("a");
    a.href = url;
    a.download = nome || "documento";
    document.body.appendChild(a);
    a.click();
    a.remove();
    return;
  }
  window.open(url, "_blank");
};

export const IdDocsSection = ({ employeeId }) => {
  const { user } = useAuth();
  const canEdit = ["admin", "manager"].includes(user?.role);
  const [slots, setSlots] = useState([]);
  const [date, setDate] = useState({});
  const inputs = useRef({});

  const load = useCallback(() => {
    api.get(`/employees/${employeeId}/id-docs`).then((r) => {
      setSlots(r.data);
      const d = {};
      r.data.forEach((s) => (s.files || []).forEach((f) => {
        d[f.id] = { issue_date: f.issue_date || "", expiry_date: f.expiry_date || "" };
      }));
      setDate(d);
    }).catch(() => {});
  }, [employeeId]);

  useEffect(() => { load(); }, [load]);

  const carica = async (kind, files) => {
    if (!files?.length) return;
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("kind", kind);
        fd.append("file", file);
        await api.post(`/employees/${employeeId}/id-docs`, fd);
      }
      toast.success(files.length > 1 ? `${files.length} file caricati` : "Documento caricato");
      load();
    } catch (err) { toast.error(errMsg(err)); }
    if (inputs.current[kind]) inputs.current[kind].value = "";
  };

  const apri = async (f, scarica = false) => {
    try {
      const res = await api.get(
        `/employees/${employeeId}/id-docs/file/${f.id}${scarica ? "?download=1" : ""}`,
        { responseType: "blob" });
      apriBlob(res.data, f.name, scarica);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const salvaDate = async (f) => {
    try {
      await api.patch(`/employees/${employeeId}/id-docs/doc/${f.id}`, date[f.id] || {});
      toast.success("Date aggiornate");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const elimina = async (f) => {
    if (!window.confirm(`Eliminare "${f.name}"?`)) return;
    try {
      await api.delete(`/employees/${employeeId}/id-docs/doc/${f.id}`);
      toast.success("Documento eliminato");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="space-y-2" data-testid="card-id-docs">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Documenti Identificativi
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        {slots.map((s) => (
          <div key={s.kind} className="rounded-sm border border-border p-2.5 space-y-2"
            data-testid={`id-doc-${s.kind}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-semibold">{s.label}</div>
              {canEdit && (
                <>
                  <input type="file" multiple className="hidden"
                    ref={(el) => { inputs.current[s.kind] = el; }}
                    data-testid={`id-doc-file-${s.kind}`}
                    onChange={(ev) => carica(s.kind, Array.from(ev.target.files || []))} />
                  <Button size="sm" variant="outline" className="h-8 rounded-sm"
                    data-testid={`id-doc-upload-${s.kind}`}
                    onClick={() => inputs.current[s.kind]?.click()}>
                    <UploadSimple size={14} className="mr-1.5" /> Carica
                  </Button>
                </>
              )}
            </div>

            {(s.files || []).length === 0 && (
              <div className="text-[11px] text-muted-foreground" data-testid={`id-doc-empty-${s.kind}`}>
                Nessun documento caricato
              </div>
            )}

            {(s.files || []).map((f) => (
              <div key={f.id} className="rounded-sm border border-border/70 bg-secondary/40 p-2 space-y-1.5"
                data-testid={`id-doc-item-${f.id}`}>
                <div className="text-[11px] font-medium truncate" title={f.name}>{f.name}</div>
                <div className="text-[10px] text-muted-foreground truncate">
                  caricato da {f.uploaded_by}
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  <div>
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Rilascio</div>
                    <Input type="date" className="h-8 rounded-sm text-xs mt-0.5" disabled={!canEdit}
                      data-testid={`id-doc-issue-${f.id}`}
                      value={date[f.id]?.issue_date || ""}
                      onChange={(ev) => setDate({ ...date, [f.id]: { ...date[f.id], issue_date: ev.target.value } })} />
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Scadenza</div>
                    <Input type="date" className="h-8 rounded-sm text-xs mt-0.5" disabled={!canEdit}
                      data-testid={`id-doc-expiry-${f.id}`}
                      value={date[f.id]?.expiry_date || ""}
                      onChange={(ev) => setDate({ ...date, [f.id]: { ...date[f.id], expiry_date: ev.target.value } })} />
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs"
                    data-testid={`id-doc-view-${f.id}`} onClick={() => apri(f)}>
                    <Eye size={13} className="mr-1" /> Apri
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs"
                    data-testid={`id-doc-download-${f.id}`} onClick={() => apri(f, true)}>
                    <DownloadSimple size={13} className="mr-1" /> Scarica
                  </Button>
                  {canEdit && (
                    <>
                      <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs"
                        data-testid={`id-doc-save-${f.id}`} onClick={() => salvaDate(f)}>
                        <FloppyDisk size={13} className="mr-1" /> Salva date
                      </Button>
                      <button type="button" className="text-rose-600 hover:text-rose-700 p-1" title="Elimina"
                        data-testid={`id-doc-delete-${f.id}`} onClick={() => elimina(f)}>
                        <Trash size={15} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default IdDocsSection;
